import React, { useEffect, useMemo } from 'react';
import { Card, Spin, Select } from 'antd';
import type { CountryCreate, CountryUpdate } from '../types/country.types';
import { useCountryForm } from '../hooks/use-country-form.hook';

import FormBuilder, { FormField } from '../../../../common/FormBuilder';
import { useTranslation } from 'react-i18next';
import { DefaultValues } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { Input } from 'antd';
import moment from 'moment-timezone';

interface CountryFormProps {
  defaultValues?: DefaultValues<CountryCreate | CountryUpdate>;
  onFormSubmit: (data: CountryCreate | CountryUpdate, resetForm: () => void) => void;
  isLoading: boolean;
  columns?: 1 | 2 | 3 | 4;
  validationErrors?: ValidationError[];
}

function CountryFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  columns = 2,
  validationErrors = [],
}: CountryFormProps) {
  const { t } = useTranslation();

  const { control, errors, resetForm, formSubmitHandler, reset, setError, setValue } =
    useCountryForm({
      defaultValues,
      onSubmit: (data) => onFormSubmit(data, resetForm),
    });

  // Get timezone options from moment-timezone
  const timezoneOptions = useMemo(() => {
    return moment.tz.names().map((timezone: string) => ({
      label: timezone,
      value: timezone,
    }));
  }, []);

  // Reset form values when defaultValues change
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  // Handle API validation errors
  useEffect(() => {
    validationErrors.forEach((error) => {
      setError(error.path as any, {
        type: 'manual',
        message: error.message,
      });
    });
  }, [validationErrors, setError]);

  const formFields: FormField<CountryCreate | CountryUpdate>[] = [
    {
      name: 'name',
      label: t('countries.name.label'),
      required: true,
      errorMsg: errors?.name?.message as string | undefined,
      rules: {
        required: 'Name is required',
        minLength: { value: 3, message: 'Name must be at least 3 characters' },
        maxLength: { value: 50, message: 'Name must be at most 50 characters' },
      },
      render: (field) => (
        <Input {...field} disabled={isLoading} placeholder={t('countries.name.placeholder')} />
      ),
    },
    {
      name: 'code',
      label: t('countries.code.label'),
      required: true,
      errorMsg: errors?.code?.message as string | undefined,
      rules: { required: 'Code is required' },
      render: (field) => (
        <Input {...field} disabled={isLoading} placeholder={t('countries.code.placeholder')} />
      ),
    },
    {
      name: 'timezone',
      label: t('countries.timezone.label'),
      required: false,
      errorMsg: errors?.timezone?.message as string | undefined,
      rules: {},
      render: (field) => (
        <Select
          {...field}
          disabled={isLoading}
          placeholder={t('countries.timezone.placeholder')}
          options={timezoneOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
          }
          allowClear
        />
      ),
    },
  ];

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <FormBuilder
          formFields={formFields}
          control={control}
          errors={errors}
          onSubmit={formSubmitHandler()}
          submitButtonText={t('common.save')}
          isLoading={isLoading}
          columns={columns}
        />
      </Card>
    </Spin>
  );
}

// Custom comparison function for memoization
function areEqual(prevProps: CountryFormProps, nextProps: CountryFormProps): boolean {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onFormSubmit === nextProps.onFormSubmit &&
    prevProps.columns === nextProps.columns &&
    JSON.stringify(prevProps.defaultValues) === JSON.stringify(nextProps.defaultValues) &&
    JSON.stringify(prevProps.validationErrors) === JSON.stringify(nextProps.validationErrors)
  );
}

export default React.memo(CountryFormComponent, areEqual);
